/**
 * The two password forms, in their own module.
 *
 * Separate from forms.jsx because the account menu in App.jsx offers "Change
 * password", and App is in the entry bundle. Importing all of forms.jsx to
 * reach one dialog dragged forty kilobytes of branch, shift, holiday and
 * employee forms back into the first download, which is most of what splitting
 * the routes had just removed.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { useOverlay } from '../lib/overlay.jsx';
import { Icon } from '../lib/icons.jsx';
import { Field, Switch } from './ui.jsx';
import { FormShell } from './FormShell.jsx';
import { checkPassword, strengthOf, RULES } from '../lib/password.js';

/* ------------------------------------------------------------------ password */

/** The rules, ticking green as they are met. */
export function PasswordRules({ value, employee }) {
  const { results } = checkPassword(value, employee);
  const strength = strengthOf(value, employee);

  return (
    <div className="pw-rules">
      {value && (
        <div className="pw-strength">
          <div className={`pw-bar t-${strength.tone}`}>
            <span style={{ width: `${((strength.score + 1) / 5) * 100}%` }} />
          </div>
          <span className={`txt-sm pw-label t-${strength.tone}`}>{strength.label}</span>
        </div>
      )}
      <ul>
        {RULES.map((r) => (
          <li key={r.id} className={results[r.id] ? 'met' : ''}>
            <Icon name={results[r.id] ? 'check' : 'dot'} />
            {r.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Changing your own password.
 *
 * Asks for the current one, which is what makes this different from HR setting
 * somebody else's: proving you already hold it is the only reason this can be
 * done without anybody's approval.
 */
export function ChangePasswordForm({ close }) {
  const { user } = useAuth();
  const { toast } = useOverlay();
  const [f, setF] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const set = (p) => setF((x) => ({ ...x, ...p }));

  const change = useMutation({
    mutationFn: (body) => post('/auth/change-password', body),
    onSuccess: () => {
      close();
      toast('Password changed. Your other sessions have been signed out.', 'good');
    },
  });

  const { ok } = checkPassword(f.newPassword, user);
  const matches = f.newPassword.length > 0 && f.newPassword === f.confirm;
  const ready = ok && matches && f.currentPassword.length > 0;

  const submit = () => {
    if (!ready) return;
    change.mutate({ currentPassword: f.currentPassword, newPassword: f.newPassword });
  };

  return (
    <FormShell
      id="pw-form" close={close} onSubmit={submit}
      busy={change.isPending} error={change.error}
      submitLabel="Change password"
    >
      <Field label="Current password">
        <input
          type="password" className="inp" required autoComplete="current-password" autoFocus
          value={f.currentPassword} onChange={(e) => set({ currentPassword: e.target.value })}
        />
      </Field>

      <Field label="New password">
        <input
          type="password" className="inp" required autoComplete="new-password"
          value={f.newPassword} onChange={(e) => set({ newPassword: e.target.value })}
        />
      </Field>

      <PasswordRules value={f.newPassword} employee={user} />

      <Field
        label="Confirm new password"
        hint={f.confirm && !matches ? 'These do not match.' : undefined}
      >
        <input
          type="password" className="inp" required autoComplete="new-password"
          value={f.confirm} onChange={(e) => set({ confirm: e.target.value })}
        />
      </Field>

      <div className="hint mt-8">
        <Icon name="info" /> Every other device you are signed in on will be signed out. The usual reason to
        change a password is thinking somebody else has it.
      </div>
    </FormShell>
  );
}

/**
 * HR setting a password for somebody who cannot sign in.
 *
 * There is no self-service reset link — this deployment only sends mail if SMTP
 * has been configured, and a reset link that silently goes nowhere is worse than
 * no link. So the locked-out employee asks HR, who set one here and read it out.
 * It is shown once and stored only as a hash.
 */
export function ResetPasswordForm({ close, employee }) {
  const { toast } = useOverlay();
  const qc = useQueryClient();
  const [mode, setMode] = useState('generate');
  const [password, setPassword] = useState('');
  const [mustChange, setMustChange] = useState(true);
  const [issued, setIssued] = useState(null);

  const reset = useMutation({
    mutationFn: (body) => post(`/employees/${employee.id}/reset-password`, body),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['employee', employee.id] });
      setIssued(res.data);
    },
  });

  const { ok } = checkPassword(password, employee);
  const ready = mode === 'generate' || ok;

  const submit = () => {
    if (issued || !ready) return;
    reset.mutate({ mustChange, ...(mode === 'manual' ? { password } : {}) });
  };

  // Once it exists, the only thing that matters is handing it over.
  if (issued) {
    return (
      <FormShell id="reset-form" close={close} onSubmit={close} submitLabel="Done">
        <div className="txt-sm" style={{ color: 'var(--text-2)', lineHeight: 1.65 }}>
          Give this to {issued.fullName} directly. It is stored hashed, so this is the only time it can be read —
          if it is lost, do this again.
        </div>
        <div style={{ background: 'var(--surface-3)', borderRadius: 12, padding: 16, margin: '14px 0' }}>
          <div className="txt-sm muted">Work email</div>
          <div className="mono" style={{ fontSize: 15, fontWeight: 650, marginBottom: 10 }}>{issued.email}</div>
          <div className="txt-sm muted">Password</div>
          <div className="mono" style={{ fontSize: 18, fontWeight: 750, letterSpacing: '.02em' }}>{issued.password}</div>
        </div>
        <button
          type="button" className="btn btn-sm"
          onClick={() => {
            navigator.clipboard?.writeText(`${issued.email} / ${issued.password}`);
            toast('Copied to the clipboard');
          }}
        >
          <Icon name="copy" />Copy both
        </button>
        <div className="hint mt-14">
          <Icon name="info" /> Their other sessions have been signed out.
          {issued.mustChangePassword ? ' They will choose their own on the next sign-in.' : ''}
        </div>
      </FormShell>
    );
  }

  return (
    <FormShell
      id="reset-form" close={close} onSubmit={submit}
      busy={reset.isPending} error={reset.error}
      submitLabel="Reset password"
    >
      <div className="check-item" style={{ marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <div className="ct">{employee.fullName}</div>
          <div className="cs">{employee.employeeCode} · {employee.email}</div>
        </div>
      </div>

      <div className="chip-row" style={{ marginBottom: 14 }}>
        <button
          type="button" className={`chip${mode === 'generate' ? ' on' : ''}`}
          onClick={() => setMode('generate')}
        >
          Generate one
        </button>
        <button
          type="button" className={`chip${mode === 'manual' ? ' on' : ''}`}
          onClick={() => setMode('manual')}
        >
          Type one
        </button>
      </div>

      {mode === 'manual' ? (
        <>
          <Field label="New password" hint="They will be asked to replace it, so it does not need to be memorable.">
            <input
              className="inp" autoFocus autoComplete="off"
              value={password} onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <PasswordRules value={password} employee={employee} />
        </>
      ) : (
        <div className="hint" style={{ marginBottom: 14 }}>
          <Icon name="info" /> A sixteen-character password will be generated and shown once. Characters that are
          misread aloud — l, 1, O, 0 — are left out.
        </div>
      )}

      <div className="flex items-c gap-10" style={{ padding: '9px 0', borderTop: '1px solid var(--border)' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 13.2 }}>Ask them to change it on first sign-in</div>
          <div className="txt-sm muted">
            You will know this password, so it is shared until they replace it.
          </div>
        </div>
        <Switch on={mustChange} onChange={setMustChange} />
      </div>
    </FormShell>
  );
}

